import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { events } from "../lib/analytics";
import { Reveal } from "../components/Reveal";
import { MenuItemCard } from "../components/MenuItemCard";

export default function MenuPage() {
  const { t, localName, localDesc, lang } = useSite();
  const [menu, setMenu] = useState([]);
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
    events.menuView("page");
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return menu
      .filter((c) => active === "all" || c.id === active)
      .map((c) => ({
        ...c,
        items: c.items.filter((it) => !q || localName(it).toLowerCase().includes(q) || (localDesc(it) || "").toLowerCase().includes(q)),
      }))
      .filter((c) => c.items.length > 0);
  }, [menu, active, query, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = menu.reduce((n, c) => n + c.items.length, 0);
  const title = t("menuTitle");

  return (
    <div className="pt-32 sm:pt-40 pb-24" data-testid="menu-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="glow-gold w-[28rem] h-[28rem] -left-40 -top-32 opacity-50" />
        <Reveal>
          <p className="eyebrow mb-5"><span className="kicker-line" />Cafe Del Nord · {total} {t("items")}</p>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[0.9] mb-6" >
            <span className="text-outline">{title}</span>
          </h1>
          <p className="text-white/55 max-w-xl mb-10 text-base sm:text-lg">{t("menuSubtitle")}</p>
        </Reveal>
      </div>

      {/* Sticky chips + search */}
      <div className="sticky top-16 sm:top-20 z-40 backdrop-blur-xl bg-[#080706]/85 border-y border-white/10 py-3" data-testid="menu-sticky-nav">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center gap-3">
          <div className="flex gap-2 overflow-x-auto pb-1 flex-1 [scrollbar-width:none]">
            <button className={`cat-chip ${active === "all" ? "active" : ""}`} onClick={() => setActive("all")} data-testid="cat-chip-all">{t("all")}</button>
            {menu.map((c) => (
              <button key={c.id} className={`cat-chip ${active === c.id ? "active" : ""}`} onClick={() => { setActive(c.id); events.menuView(c.name); }} data-testid={`cat-chip-${c.slug}`}>
                {localName(c)}
              </button>
            ))}
          </div>
          <div className="relative shrink-0 w-40 sm:w-64">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search")} className="search-field" data-testid="menu-search-input" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-14">
        {/* Content */}
        <div className="space-y-24">
          {filtered.length === 0 && menu.length > 0 && (
            <p className="text-white/40 text-center py-16" data-testid="menu-no-results">{t("noResults")}</p>
          )}
          {filtered.map((cat, ci) => (
            <section key={cat.id} data-testid={`menu-category-${cat.slug}`}>
              <Reveal>
                <div className="flex items-end justify-between gap-4 mb-8 border-b border-white/10 pb-5">
                  <div className="flex items-baseline gap-4">
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">{localName(cat)}</h2>
                  </div>
                  <span className="eyebrow !text-white/35 hidden sm:block">{cat.items.length} {t("items")}</span>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                {cat.items.map((item, i) => (
                  <Reveal key={item.id} delay={(i % 2) * 0.08} className={i === 0 ? "sm:col-span-2" : ""}>
                    <MenuItemCard item={item} index={i} variant={i === 0 ? "wide" : ""} />
                  </Reveal>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
